import { Controller, useForm } from 'react-hook-form';
import React, { Fragment, useEffect, useState } from 'react';

import CardInput from './CardInput';
import { CloseLetterReqDTO } from '../dtos/closeLetterReqDTO';
import IndexError from './IndexError';
import Input from './Input';
import { letterState } from '../atoms/letter';
import { registerState } from '../atoms/register';
import { useCloseLetter } from '../hooks/letters';
import { useRecoilValue } from 'recoil';
import { useTranslation } from 'react-i18next';

interface StepConfirmProps {
	title: string;
	isActive?: boolean;
}
function StepConfirm({ title, isActive }: StepConfirmProps) {
	const { t } = useTranslation();

	const registers = useRecoilValue(registerState);
	const letter = useRecoilValue(letterState);
	const [closeLetter, setCloseLetter] = useState<CloseLetterReqDTO>();

	const {
		isFetching,
		error,
		refetch,
		data: closeLetterResponse,
	} = useCloseLetter(closeLetter);

	const {
		control,
		handleSubmit,
		formState: { errors },
	} = useForm();

	useEffect(() => {
		if (closeLetter && isActive) {
			refetch();
		}
	}, [closeLetter, isActive, refetch]);

	const onSubmit = (data: any) => {
		if (letter) {
			if (registers.isOK === false) {
				setCloseLetter({
					email: data.nonRequiredEmail === '' ? null : data.nonRequiredEmail,
					phoneNumber: data.phone,
					referenceCode: letter!.referenceCode,
					status: 'EARLY_CLOSE',
				});
			} else {
				setCloseLetter({
					email: data.requiredEmail,
					referenceCode: letter!.referenceCode,
					status: 'CLOSED',
				});
			}
		}
	};

	return (
		<div className='container confirm-index'>
			<div className='row'>
				<div className='col-lg-10 col-xxl-8 offset-lg-1 offset-xxl-2'>
					<h2 className='visually-hidden'>{title}</h2>
					<div className='confirm-index__body'>
						{(error as any)?.response.status === 500 ? (
							<IndexError />
						) : registers.isOK ? (
							<Fragment>
								<div
									data-test-id='confirm-index-ask-email'
									className='confirm-index__title'
								>
									<svg
										xmlns='http://www.w3.org/2000/svg'
										fill='none'
										viewBox='0 0 24 24'
										strokeWidth='1.5'
										stroke='currentColor'
										className='icon'
									>
										<path
											strokeLinecap='round'
											strokeLinejoin='round'
											d='M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z'
										/>
									</svg>

									<h1>{t('confirm-index.register.ok.title')}</h1>
								</div>
								<Fragment>
									{closeLetterResponse?.status === 200 && closeLetter ? (
										<h2>{t('confirm-index.register.email.sent')}</h2>
									) : (
										<Fragment>
											<h3>{t('confirm-index.thanks')}</h3>
											<h4>{t('confirm-index.register.ok.subtitle')}</h4>
											<form onSubmit={handleSubmit(onSubmit)}>
												<Controller
													render={({ field }) => (
														<CardInput
															field={field}
															title={t('confirm-index.register.email.header')}
															subTitle={t(
																'confirm-index.register.email.header.caption'
															)}
															errorMessage={errors.requiredEmail?.message}
															legal={t('confirm-index.register.email.legal')}
															isLoading={isFetching}
														/>
													)}
													name='requiredEmail'
													control={control}
													rules={{
														required: t(
															'confirm-index.register.email.error.required'
														),
														pattern: {
															value:
																/^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/,
															message: t(
																'confirm-index.register.email.error.pattern'
															),
														},
													}}
													defaultValue={''}
												/>
											</form>
										</Fragment>
									)}
								</Fragment>
							</Fragment>
						) : (
							<Fragment>
								<div
									data-test-id='confirm-index-ask-phone'
									className='confirm-index__title'
								>
									<svg
										xmlns='http://www.w3.org/2000/svg'
										fill='none'
										viewBox='0 0 24 24'
										strokeWidth='1.5'
										stroke='currentColor'
										className='icon'
									>
										<path
											strokeLinecap='round'
											strokeLinejoin='round'
											d='M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z'
										/>
									</svg>
									<h1>{t('confirm-index.register.not-ok.title')}</h1>
								</div>
								<Fragment>
									{closeLetterResponse?.status === 200 && closeLetter ? (
										<Fragment>
											<h3>{t('confirm-index.register.phone.sent')}</h3>
											<p>{t('confirm-index.register.phone.sent.info')}</p>
											<h3>{t('confirm-index.register.phone.more.info')}</h3>
											<p>
												{t(
													'confirm-index.register.phone.more.info.contact.phone'
												)}
												&nbsp;
												<a
													href={`tel:${t(
														'confirm-index.register.phone.more.info.contact.phone.value'
													)}`}
												>
													{t(
														'confirm-index.register.phone.more.info.contact.phone.value'
													)}
												</a>
												<br />
												{t(
													'confirm-index.register.phone.more.info.contact.form.one'
												)}
												&nbsp;
												<a
													target='_blank'
													rel='noopener noreferrer'
													href={t(
														'confirm-index.register.phone.more.info.contact.form.link'
													)}
												>
													{t(
														'confirm-index.register.phone.more.info.contact.form.two'
													)}
												</a>
											</p>
										</Fragment>
									) : (
										<Fragment>
											<h3>{t('confirm-index.register.not-ok.subtitle')}</h3>
											<form onSubmit={handleSubmit(onSubmit)}>
												<CardInput
													subTitle={t(
														'confirm-index.register.phone.header.caption'
													)}
													legal={t('confirm-index.register.phone.legal')}
													isLoading={isFetching}
												>
													<div className='container-fluid p-0'>
														<div className='row'>
															<div className='col-md-6'>
																<Controller
																	render={({ field }) => (
																		<Input
																			field={field}
																			label={t(
																				'confirm-index.register.phone.header'
																			)}
																		/>
																	)}
																	name='phone'
																	control={control}
																	rules={{
																		required: t(
																			'confirm-index.register.phone.error.required'
																		),
																		pattern: {
																			value:
																				/^[+]*[00]{0,2}[0-9]{0,3}[0-9]{9}|0[0-9]{9}$/,
																			message: t(
																				'confirm-index.register.phone.error.pattern'
																			),
																		},
																	}}
																	defaultValue={''}
																/>
																{errors.phone && (
																	<span
																		data-test-id='card-input-error'
																		className='input__error'
																	>
																		<p className='arrow-container mb-4'>
																			<span className='up-arrow'></span>
																		</p>
																		<div className='input__error__block'>
																			{errors.phone?.message as string}
																		</div>
																	</span>
																)}
															</div>
															<div className='col-md-6'>
																<Controller
																	render={({ field }) => (
																		<Input
																			field={field}
																			label={t(
																				'confirm-index.register.email.header'
																			)}
																		/>
																	)}
																	name='nonRequiredEmail'
																	control={control}
																	rules={{
																		pattern: {
																			value:
																				/^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/,
																			message: t(
																				'confirm-index.register.email.error.pattern'
																			),
																		},
																	}}
																	defaultValue={''}
																/>
																{errors.nonRequiredEmail && (
																	<span
																		data-test-id='card-input-error'
																		className='input__error'
																	>
																		<p className='arrow-container mb-4'>
																			<span className='up-arrow'></span>
																		</p>
																		<div className='input__error__block'>
																			{
																				errors.nonRequiredEmail
																					?.message as string
																			}
																		</div>
																	</span>
																)}
															</div>
														</div>
														<div className='w-100 d-flex mt-4 justify-content-end'>
															<button
																type='submit'
																data-test-id='card-input-btn-submit'
																className='btn btn--primary w-auto text-end'
																disabled={isFetching}
															>
																{t('common.send')}
															</button>
														</div>
													</div>
												</CardInput>
											</form>
										</Fragment>
									)}
								</Fragment>
							</Fragment>
						)}
					</div>
				</div>
			</div>
		</div>
	);
}

export default StepConfirm;
